import React, { useState, useMemo, useRef, forwardRef, useImperativeHandle, useEffect } from 'react';
import { MessageCircle, Clock, CheckCircle, AlertCircle, Edit2, X, Plus, Search, Languages, User, CircleDollarSign, RefreshCcw, FileVideo } from 'lucide-react';
import { apiService, updateChatRoomUserId } from '../../services/api';
import { updateChatRoomTags } from '../../services/api';
import { ChatRoom, ChatRoomsResponse } from '../../types';

interface ChatRoomListProps {
  chatRooms: ChatRoomsResponse;
  selectedUserEmail: string | null;
  onSelectChatRoom: (userEmail: string) => void;
  onRefresh: () => Promise<void>;
  onUpdateChatRoom?: (userEmail: string, updates: Partial<ChatRoom>) => void;
}

export interface ChatRoomListRef {
  scrollToSection: (section: 'unreplied' | 'replied' | 'has_new') => void;
  scrollToUserEmail: (userEmail: string) => void;
}

const ChatRoomList = forwardRef<ChatRoomListRef, ChatRoomListProps>(({ 
  chatRooms, 
  selectedUserEmail, 
  onSelectChatRoom, 
  onRefresh, 
  onUpdateChatRoom 
}, ref) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingTags, setEditingTags] = useState<string | null>(null);
  const [editingTagsValue, setEditingTagsValue] = useState<string>('');
  const [updatingTags, setUpdatingTags] = useState<string | null>(null);
  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const [editingUserIdValue, setEditingUserIdValue] = useState<string>('');
  const [updatingUserId, setUpdatingUserId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [activeTab, setActiveTab] = useState<'unreplied' | 'replied' | 'has_new' | null>(null);
  const [copiedEmail, setCopiedEmail] = useState<string | null>(null);

  // 各个分类的ref
  const unrepliedRef = useRef<HTMLDivElement>(null);
  const repliedRef = useRef<HTMLDivElement>(null);
  const hasNewRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // 暴露滚动方法给父组件
  useImperativeHandle(ref, () => ({
    scrollToSection: (section: 'unreplied' | 'replied' | 'has_new') => {
      setActiveTab(section);
      let targetRef;
      switch (section) {
        case 'unreplied':
          targetRef = unrepliedRef;
          break;
        case 'replied':
          targetRef = repliedRef;
          break;
        case 'has_new':
          targetRef = hasNewRef;
          break;
      }
      
      if (targetRef.current) {
        targetRef.current.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start' 
        });
      }
    },
    scrollToUserEmail: (userEmail: string) => {
      // 查找指定邮箱所在的分类
      let targetSection: 'unreplied' | 'replied' | 'has_new' | null = null;
      
      // 检查各个分类中是否包含该邮箱
      if (chatRooms.unreplied.some(room => room.user_email === userEmail)) {
        targetSection = 'unreplied';
      } else if (chatRooms.has_new?.some(room => room.user_email === userEmail)) {
        targetSection = 'has_new';
      } else if (chatRooms.replied.some(room => room.user_email === userEmail)) {
        targetSection = 'replied';
      }
      
      // 如果找到了目标分类，自动切换到该Tab
      if (targetSection) {
        console.log(`🎯 自动切换到 ${targetSection} Tab，因为邮箱 ${userEmail} 在该分类中`);
        
        setActiveTab(targetSection);
        let targetRef;
        switch (targetSection) {
          case 'unreplied':
            targetRef = unrepliedRef;
            break;
          case 'replied':
            targetRef = repliedRef;
            break;
          case 'has_new':
            targetRef = hasNewRef;
            break;
        }
        
        if (targetRef.current && scrollContainerRef.current) {
          const container = scrollContainerRef.current;
          const target = targetRef.current;
          const containerRect = container.getBoundingClientRect();
          const targetRect = target.getBoundingClientRect();
          const scrollTop = container.scrollTop + (targetRect.top - containerRect.top);
          
          container.scrollTo({
            top: scrollTop,
            behavior: 'smooth'
          });
        }
      } else {
        console.log(`❌ 未找到邮箱 ${userEmail} 所在的分类`);
      }
    }
  }));

  // 初始化activeTab
  useEffect(() => {
    if (!activeTab) {
      if (chatRooms.unreplied.length > 0) {
        setActiveTab('unreplied');
      } else if (chatRooms.has_new?.length > 0) {
        setActiveTab('has_new');
      } else if (chatRooms.replied.length > 0) {
        setActiveTab('replied');
      }
    }
  }, [chatRooms, activeTab]);

  // 滚动监听，自动更新activeTab
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    const handleScroll = () => {
      const containerRect = scrollContainer.getBoundingClientRect();
      const containerTop = containerRect.top;
      const containerHeight = containerRect.height;
      const containerBottom = containerTop + containerHeight;

      // 检查各个分类区域的位置，按优先级顺序排列
      const sections = [
        { ref: unrepliedRef, tab: 'unreplied' as const, hasContent: chatRooms.unreplied.length > 0 },
        { ref: hasNewRef, tab: 'has_new' as const, hasContent: (chatRooms.has_new?.length || 0) > 0 },
        { ref: repliedRef, tab: 'replied' as const, hasContent: chatRooms.replied.length > 0 }
      ];

      let newActiveTab = activeTab;

      // 寻找第一个在可视区域内有足够内容的分类
      for (const section of sections) {
        if (!section.hasContent || !section.ref.current) continue;
        
        const sectionRect = section.ref.current.getBoundingClientRect();
        const sectionTop = sectionRect.top;
        const sectionBottom = sectionRect.bottom;

        // 计算该区域在可视区域内的面积
        const visibleTop = Math.max(sectionTop, containerTop);
        const visibleBottom = Math.min(sectionBottom, containerBottom);
        const visibleArea = Math.max(0, visibleBottom - visibleTop);

        // 如果该区域在可视区域内有显著内容（至少50px或者占该区域20%以上），则设为活跃tab
        const sectionHeight = sectionBottom - sectionTop;
        const visibleRatio = visibleArea / sectionHeight;
        
        if (visibleArea >= 50 || visibleRatio >= 0.2) {
          newActiveTab = section.tab;
          break; // 选择第一个符合条件的，这样会优先选择顶部的分类
        }
      }

      if (newActiveTab !== activeTab) {
        setActiveTab(newActiveTab);
      }
    };

    scrollContainer.addEventListener('scroll', handleScroll);
    
    // 初始调用一次
    handleScroll();

    return () => {
      scrollContainer.removeEventListener('scroll', handleScroll);
    };
  }, [chatRooms, activeTab]);

  // 处理tab点击
  const handleTabClick = (tab: 'unreplied' | 'replied' | 'has_new') => {
    // 检查是否有内容，如果没有则不执行操作
    let hasContent = false;
    switch (tab) {
      case 'unreplied':
        hasContent = chatRooms.unreplied.length > 0;
        break;
      case 'replied':
        hasContent = chatRooms.replied.length > 0;
        break;
      case 'has_new':
        hasContent = (chatRooms.has_new?.length || 0) > 0;
        break;
    }
    
    if (!hasContent) return;
    
    setActiveTab(tab);
    let targetRef;
    switch (tab) {
      case 'unreplied':
        targetRef = unrepliedRef;
        break;
      case 'replied':
        targetRef = repliedRef;
        break;
      case 'has_new':
        targetRef = hasNewRef;
        break;
    }
    
    if (targetRef.current && scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const target = targetRef.current;
      const containerRect = container.getBoundingClientRect();
      const targetRect = target.getBoundingClientRect();
      const scrollTop = container.scrollTop + (targetRect.top - containerRect.top);
      
      container.scrollTo({
        top: scrollTop,
        behavior: 'smooth'
      });
    }
  };

  // 过滤和排序聊天室的逻辑
  const filteredChatRooms = useMemo(() => {
    const filterAndSortRooms = (rooms: ChatRoom[], category: 'unreplied' | 'replied' | 'has_new') => {
      let filteredRooms = rooms;
      
      // 如果有搜索关键词，先过滤
      if (searchTerm.trim()) {
        const searchLower = searchTerm.toLowerCase();
        filteredRooms = rooms.filter(room => {
          return (
            room.user_email.toLowerCase().includes(searchLower) ||
            room.user_name?.toLowerCase().includes(searchLower) ||
            room.tags.some(tag => tag.toLowerCase().includes(searchLower))
          );
        });
      }
      
      // 取两个时间字段中的最新值进行排序
      return filteredRooms.sort((a, b) => {
        // 获取A聊天室的最新活动时间
        const userTimeA = a.latest_user_email_time ? new Date(a.latest_user_email_time).getTime() : 0;
        const replyTimeA = a.latest_reply_email_time ? new Date(a.latest_reply_email_time).getTime() : 0;
        const timeA = Math.max(userTimeA, replyTimeA);
        
        // 获取B聊天室的最新活动时间
        const userTimeB = b.latest_user_email_time ? new Date(b.latest_user_email_time).getTime() : 0;
        const replyTimeB = b.latest_reply_email_time ? new Date(b.latest_reply_email_time).getTime() : 0;
        const timeB = Math.max(userTimeB, replyTimeB);
        
        return timeB - timeA; // 降序排序，最新活动的在前面
      });
    };

    return {
      unreplied: filterAndSortRooms(chatRooms.unreplied || [], 'unreplied'),
      replied: filterAndSortRooms(chatRooms.replied || [], 'replied'),
      has_new: filterAndSortRooms(chatRooms.has_new || [], 'has_new')
    };
  }, [chatRooms, searchTerm]);

  const handleSelectRoom = (room: ChatRoom) => {
    onSelectChatRoom(room.user_email);
  };

  const handleRefresh = async () => {
    try {
      setLoading(true);
      await onRefresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : '刷新失败');
    } finally {
      setLoading(false);
    }
  };

  const handleEditUserId = (e: React.MouseEvent, userEmail: string, currentUserId: string) => {
    e.stopPropagation();
    setEditingUserId(userEmail);
    setEditingUserIdValue(currentUserId);
  };

  const handleSaveUserId = async (userEmail: string) => {
    try {
      setUpdatingUserId(userEmail);
      const userId = editingUserIdValue;
      await updateChatRoomUserId(userEmail, userId);
      setEditingUserId(null);
      setEditingUserIdValue('');
      
      // 不再进行完整刷新，而是通过props传递的chatRooms数据由SSE自动更新
      // 或者可以选择性地只更新这一个聊天室的数据
      console.log('✅ 用户ID更新成功，等待SSE更新或下次自然刷新');
      
      if (onUpdateChatRoom) {
        onUpdateChatRoom(userEmail, { user_id: userId });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : '更新用户ID失败');
    } finally {
      setUpdatingUserId(null);
    }
  };

  const handleCancelEditUserId = () => {
    setEditingUserId(null);
    setEditingUserIdValue('');
  };

  const handleEditTags = (e: React.MouseEvent, userEmail: string, currentTags: string[]) => {
    e.stopPropagation();
    setEditingTags(userEmail);
    setEditingTagsValue(currentTags.join(', '));
  };

  const handleSaveTags = async (userEmail: string) => {
    try {
      setUpdatingTags(userEmail);
      const tagsArray = editingTagsValue
        .split(',')
        .map(tag => tag.trim())
        .filter(tag => tag.length > 0);
      
      await updateChatRoomTags(userEmail, tagsArray);
      setEditingTags(null);
      setEditingTagsValue('');
      
      // 不再进行完整刷新，而是通过props传递的chatRooms数据由SSE自动更新
      // 或者可以选择性地只更新这一个聊天室的数据
      console.log('✅ 标签更新成功，等待SSE更新或下次自然刷新');
      
      if (onUpdateChatRoom) {
        onUpdateChatRoom(userEmail, { tags: tagsArray });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : '更新标签失败');
    } finally {
      setUpdatingTags(null);
    }
  };

  const handleCancelEditTags = () => {
    setEditingTags(null);
    setEditingTagsValue('');
  };

  const formatTime = (timeString?: string) => {
    if (!timeString) return '无';
    
    // 处理服务器返回的时间格式
    // 如果时间字符串没有时区信息，假设它是UTC时间
    let date: Date;
    if (timeString.includes('T') && !timeString.includes('Z') && !timeString.includes('+')) {
      // 格式如 "2025-07-10T06:31:41" - 假设为UTC时间
      date = new Date(timeString + 'Z');
    } else {
      date = new Date(timeString);
    }
    
    const now = new Date();
    
    // 直接使用UTC时间戳计算时间差
    const diffMs = now.getTime() - date.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays > 0) {
      return `${diffDays}天前`;
    } else if (diffHours > 0) {
      return `${diffHours}小时前`;
    } else {
      return '刚刚';
    }
  };

  // 提取账户名称（@符号前的部分）
  const getAccountName = (accountEmail?: string | null) => {
    if (!accountEmail) return null;
    const atIndex = accountEmail.indexOf('@');
    return atIndex > 0 ? accountEmail.substring(0, atIndex) : accountEmail;
  };

  // 复制邮箱地址到剪切板
  const handleCopyEmail = async (e: React.MouseEvent, email: string) => {
    e.stopPropagation(); // 防止触发聊天室选择
    try {
      await navigator.clipboard.writeText(email);
      setCopiedEmail(email);
      // 2秒后清除提示
      setTimeout(() => setCopiedEmail(null), 2000);
    } catch (err) {
      console.error('复制邮箱地址失败:', err);
      // 降级方案：使用传统的复制方法
      const textArea = document.createElement('textarea');
      textArea.value = email;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopiedEmail(email);
      // 2秒后清除提示
      setTimeout(() => setCopiedEmail(null), 2000);
    }
  };

  const renderChatRoomItem = (room: ChatRoom) => (
    <div
      key={room.id}
      className={`p-2 border-b theme-border-primary cursor-pointer theme-bg-hover theme-transition ${
        selectedUserEmail === room.user_email ? 'theme-blue-bg theme-blue-border' : ''
      }`}
      onClick={() => handleSelectRoom(room)}
    >
      <div className="space-y-1">
        {/* 第一行：用户信息和邮件统计 */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2 flex-1 min-w-0">
            <div className="flex items-center space-x-2">
              {/* 邮箱账户标记 */}
              {getAccountName(room.account_email) && (
                <span className="inline-flex items-center px-2 py-1 text-xs font-medium theme-green-bg theme-green-text rounded-full">
                  {getAccountName(room.account_email)}
                </span>
              )}
              <button
                onClick={(e) => handleCopyEmail(e, room.user_email)}
                className={`text-xs font-medium truncate hover:theme-blue-text theme-transition cursor-pointer max-w-40 ${
                  copiedEmail === room.user_email ? 'theme-green-text' : 'theme-text-primary'
                }`}
                title={copiedEmail === room.user_email ? '已复制到剪切板！' : '点击复制邮箱地址'}
              >
                {room.user_email}
              </button>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => {
                  window.open(`https://app.basedash.com/pages/64333?filters=%5B%7B%22id%22%3A%226a7dd487-55e0-42ba-bee9-e0027c6d68a8%22%2C%22attributeId%22%3A5066126%2C%22operator%22%3A%22%3D%22%2C%22content%22%3A%22${room.user_email}%22%7D%5D`, '_blank');
                }}
                className="flex items-center gap-1 px-2 py-1 theme-blue-button text-white text-xs rounded hover:bg-blue-600 theme-transition disabled:opacity-50 disabled:cursor-not-allowed"
                title="统计平台用户查询"
              >
                <>
                  <User size={10} />
                </>
              </button>
            </div>
          </div>
          <div className="flex items-center space-x-2 text-xs theme-text-secondary flex-shrink-0">
            <span className="flex items-center">
              <MessageCircle className="w-3 h-3 mr-1" />
              {room.user_emails_count}/{room.reply_emails_count}
            </span>
          </div>
        </div>
        
        {/* 第二行：用户ID */}
        <div className="flex items-center">
          {/* 用户ID区域 - 限制宽度 */}
          <div className="w-22 mr-2">
            {editingUserId === room.user_email ? (
              <div className="flex items-center" onClick={(e) => e.stopPropagation()}>
                <input
                  type="text"
                  value={editingUserIdValue}
                  onChange={(e) => setEditingUserIdValue(e.target.value)}
                  placeholder="输入用户ID"
                  className="flex-1 px-2 py-1 text-xs theme-input theme-border-focus rounded"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleSaveUserId(room.user_email);
                    } else if (e.key === 'Escape') {
                      handleCancelEditUserId();
                    }
                  }}
                />
                <button
                  onClick={() => handleSaveUserId(room.user_email)}
                  disabled={updatingUserId === room.user_email}
                  className="ml-1 p-1 text-green-600 hover:text-green-700 disabled:opacity-50"
                >
                  <CheckCircle className="w-3 h-3" />
                </button>
                <button
                  onClick={handleCancelEditUserId}
                  className="p-1 text-gray-500 hover:text-gray-700"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ) : (
              <div className="flex gap-1 items-center">
                {room.user_id ? (
                  <>
                    <span className="text-xs theme-text-secondary">用户ID:</span>
                    <span
                      className="inline-flex items-center px-1.5 py-0.5 text-xs theme-blue-bg theme-blue-text rounded cursor-pointer hover:bg-blue-200 theme-transition truncate max-w-20"
                      onClick={(e) => handleEditUserId(e, room.user_email, room.user_id || '')}
                      title={room.user_id || ''}
                    >
                      {room.user_id}
                    </span>
                    <button
                      onClick={(e) => handleEditUserId(e, room.user_email, room.user_id || '')}
                      className="inline-flex items-center p-0.5 theme-text-tertiary hover:text-gray-600 theme-transition"
                    >
                      <Edit2 className="w-3 h-3" />
                    </button>
                  </>
                ) : (
                  <button
                    onClick={(e) => handleEditUserId(e, room.user_email, '')}
                    className="inline-flex items-center px-1.5 py-0.5 text-xs theme-text-secondary border border-dashed theme-border-secondary rounded hover:border-gray-400 hover:text-gray-600 theme-transition"
                  >
                    <Plus className="w-3 h-3 mr-1" />
                    用户ID
                  </button>
                )}
              </div>
            )}
          </div>
          
          {/* 统计区域 - 紧挨着用户ID区域 */}
          <div className="flex items-center gap-1">
            {(room.user_id) ? (
              <button
              onClick={() => {
                window.open(`https://app.basedash.com/pages/64333/${room.user_id}`, '_blank');
              }}
              className="flex items-center gap-1 px-2 py-1 theme-blue-button text-white text-xs rounded hover:bg-blue-600 theme-transition disabled:opacity-50 disabled:cursor-not-allowed"
              title="用户信息查询"
            >
              <>
                <User size={10} />
              </>
            </button>
            ) : <></>}
            {(room.user_id) ? (
              <button
              onClick={() => {
                window.open(`https://app.basedash.com/pages/64332?filters=%5B%7B%22id%22%3A%224516b3bc-8c8f-4164-b43b-71f910a6d313%22%2C%22attributeId%22%3A5065798%2C%22operator%22%3A%22%3D%22%2C%22content%22%3A%22${room.user_id}%22%7D%5D`, '_blank');
              }}
              className="flex items-center gap-1 px-2 py-1 theme-blue-button text-white text-xs rounded hover:bg-blue-600 theme-transition disabled:opacity-50 disabled:cursor-not-allowed"
              title="支付信息查询"
            >
              <>
                <CircleDollarSign size={10} />
              </>
            </button>
            ) : <></>}
            {(room.user_id) ? (
              <button
              onClick={() => {
                window.open(`https://app.basedash.com/pages/64308?filters=%5B%7B%22id%22%3A%220e010a7e-7de0-4983-bf33-26da9cec9ab3%22%2C%22attributeId%22%3A5065688%2C%22operator%22%3A%22%3D%22%2C%22content%22%3A%22${room.user_id}%22%7D%5D`, '_blank');
              }}
              className="flex items-center gap-1 px-2 py-1 theme-blue-button text-white text-xs rounded hover:bg-blue-600 theme-transition disabled:opacity-50 disabled:cursor-not-allowed"
              title="视频信息查询"
            >
              <>
                <FileVideo size={10} />
              </>
            </button>
            ) : <></>}
          </div>
        </div>

        {/* 第三行：标签和时间 */}
        <div className="flex items-center justify-between">
          {/* 标签区域 */}
          <div className="flex-1 min-w-0 mr-2">
            {editingTags === room.user_email ? (
              <div className="flex items-center" onClick={(e) => e.stopPropagation()}>
                <input
                  type="text"
                  value={editingTagsValue}
                  onChange={(e) => setEditingTagsValue(e.target.value)}
                  placeholder="输入标签，用逗号分隔"
                  className="flex-1 px-2 py-1 text-xs theme-input theme-border-focus rounded"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleSaveTags(room.user_email);
                    } else if (e.key === 'Escape') {
                      handleCancelEditTags();
                    }
                  }}
                />
                <button
                  onClick={() => handleSaveTags(room.user_email)}
                  disabled={updatingTags === room.user_email}
                  className="ml-1 p-1 text-green-600 hover:text-green-700 disabled:opacity-50"
                >
                  <CheckCircle className="w-3 h-3" />
                </button>
                <button
                  onClick={handleCancelEditTags}
                  className="p-1 text-gray-500 hover:text-gray-700"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ) : (
              <div className="flex flex-wrap gap-1 items-center">
                {room.tags.length > 0 ? (
                  <>
                    <span className="text-xs theme-text-secondary">标签:</span>
                    {room.tags.slice(0, 2).map((tag, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-1.5 py-0.5 text-xs theme-blue-bg theme-blue-text rounded cursor-pointer hover:bg-blue-200 theme-transition"
                        onClick={(e) => handleEditTags(e, room.user_email, room.tags)}
                      >
                        {tag}
                      </span>
                    ))}
                    {room.tags.length > 2 && (
                      <span className="text-xs theme-text-secondary">+{room.tags.length - 2}</span>
                    )}
                    <button
                      onClick={(e) => handleEditTags(e, room.user_email, room.tags)}
                      className="inline-flex items-center p-0.5 theme-text-tertiary hover:text-gray-600 theme-transition"
                    >
                      <Edit2 className="w-3 h-3" />
                    </button>
                  </>
                ) : (
                  <button
                    onClick={(e) => handleEditTags(e, room.user_email, [])}
                    className="inline-flex items-center px-1.5 py-0.5 text-xs theme-text-secondary border border-dashed theme-border-secondary rounded hover:border-gray-400 hover:text-gray-600 theme-transition"
                  >
                    <Plus className="w-3 h-3 mr-1" />
                    标签
                  </button>
                )}
              </div>
            )}
          </div>

          {/* 时间信息 */}
          <div className="flex items-center space-x-2 text-xs theme-text-secondary flex-shrink-0">
            <div className="flex items-center">
              <Clock className="w-3 h-3 mr-1" />
              {formatTime(room.latest_user_email_time)}
            </div>
            {room.latest_reply_email_time && (
              <div className="theme-green-text">
                回复{formatTime(room.latest_reply_email_time)}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 theme-blue-text"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-center theme-red-text">
        <p>{error}</p>
        <button
          onClick={handleRefresh}
          className="mt-2 px-4 py-2 theme-blue-button text-white rounded hover:bg-blue-700"
        >
          重试
        </button>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* 搜索框 - 更紧凑 */}
      <div className="p-3 border-b theme-border-primary theme-bg-primary">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 theme-text-tertiary w-4 h-4" />
          <input
            type="text"
            placeholder="搜索用户邮箱"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 text-sm theme-input theme-border-focus rounded-lg"
          />
        </div>
      </div>

      {/* Tab导航栏 */}
      <div className="theme-bg-primary border-b theme-border-primary">
        <div className="flex">
          <button
            onClick={() => handleTabClick('unreplied')}
            disabled={chatRooms.unreplied.length === 0}
            className={`flex-1 flex items-center justify-center px-4 py-3 text-xs font-medium border-b-2 theme-transition ${
              activeTab === 'unreplied'
                ? 'border-red-500 text-red-600 theme-red-bg'
                : chatRooms.unreplied.length === 0
                ? 'border-transparent theme-text-tertiary cursor-not-allowed'
                : 'border-transparent theme-text-secondary hover:text-gray-700 theme-bg-hover'
            }`}
          >
            未回复 ({chatRooms.unreplied.length})
          </button>
          
          <button
            onClick={() => handleTabClick('has_new')}
            disabled={(chatRooms.has_new?.length || 0) === 0}
            className={`flex-1 flex items-center justify-center px-4 py-3 text-xs font-medium border-b-2 theme-transition ${
              activeTab === 'has_new'
                ? 'border-orange-500 text-orange-600 theme-yellow-bg'
                : (chatRooms.has_new?.length || 0) === 0
                ? 'border-transparent theme-text-tertiary cursor-not-allowed'
                : 'border-transparent theme-text-secondary hover:text-gray-700 theme-bg-hover'
            }`}
          >
            待回复 ({chatRooms.has_new?.length || 0})
          </button>
          
          <button
            onClick={() => handleTabClick('replied')}
            disabled={chatRooms.replied.length === 0}
            className={`flex-1 flex items-center justify-center px-4 py-3 text-xs font-medium border-b-2 theme-transition ${
              activeTab === 'replied'
                ? 'border-green-500 text-green-600 theme-green-bg'
                : chatRooms.replied.length === 0
                ? 'border-transparent theme-text-tertiary cursor-not-allowed'
                : 'border-transparent theme-text-secondary hover:text-gray-700 theme-bg-hover'
            }`}
          >
            已回复 ({chatRooms.replied.length})
          </button>
        </div>
      </div>

      <div ref={scrollContainerRef} className="flex-1 overflow-y-auto">
        {/* 未回复的聊天室 */}
        {filteredChatRooms.unreplied.length > 0 && (
          <div ref={unrepliedRef}>
            <div className="px-3 py-2 theme-red-bg border-b border-red-200">
              <h2 className="text-sm font-semibold text-red-800 flex items-center">
                未回复 ({filteredChatRooms.unreplied.length})
                {searchTerm && <span className="ml-2 text-xs">/ 共{chatRooms.unreplied.length}个</span>}
              </h2>
            </div>
            {filteredChatRooms.unreplied.map(renderChatRoomItem)}
          </div>
        )}

        {/* 待回复的聊天室 */}
        {filteredChatRooms.has_new.length > 0 && (
          <div ref={hasNewRef}>
            <div className="px-3 py-2 theme-yellow-bg border-b border-orange-200">
              <h2 className="text-sm font-semibold text-orange-800 flex items-center">
                待回复 ({filteredChatRooms.has_new.length})
                {searchTerm && <span className="ml-2 text-xs">/ 共{chatRooms.has_new.length}个</span>}
              </h2>
            </div>
            {filteredChatRooms.has_new.map(renderChatRoomItem)}
          </div>
        )}
        
        {/* 已回复的聊天室 */}
        {filteredChatRooms.replied.length > 0 && (
          <div ref={repliedRef}>
            <div className="px-3 py-2 theme-green-bg border-b border-green-200">
              <h2 className="text-sm font-semibold text-green-800 flex items-center">
                已回复 ({filteredChatRooms.replied.length})
                {searchTerm && <span className="ml-2 text-xs">/ 共{chatRooms.replied.length}个</span>}
              </h2>
            </div>
            {filteredChatRooms.replied.map(renderChatRoomItem)}
          </div>
        )}
      </div>
    </div>
  );
});

export default ChatRoomList;
